import React from 'react';
import * as Yup from 'yup';

import School, { SchoolEditableFields } from '../../../types/School';
import City from '../../../types/City';
import SchoolType from '../../../types/SchoolType';

import handleApiErrors from '../../../lib/apiClient/handleErrors';
import updateSchool from '../../../lib/apiClient/schools/update';
import deleteSchool from '../../../lib/apiClient/schools/delete';

import InputCell from '../../common/ui/Table/Cells/InputCell';
import SearchCell from '../../common/ui/Table/Cells/SearchCell';
import Row from '../../common/ui/Table/Row';

interface Props {
  school: School;
  cities: Record<number, City>;
  schoolTypes: Record<number, SchoolType>;
  unauthorizedCallback: () => void;
  showError: (text: string) => void;
}

const SchoolsTableRow = ({
  school: { id, name, code, cityId, typeId },
  cities,
  schoolTypes,
  unauthorizedCallback,
  showError,
}: Props) => {
  const update = (school: SchoolEditableFields) =>
    handleApiErrors(updateSchool(id, school), unauthorizedCallback, showError);

  const destroy = () =>
    handleApiErrors(deleteSchool(id), unauthorizedCallback, showError);

  const citiesNames = Object.values(cities).map((city) => city.name);
  const citiesSelectOptions = Object.values(cities).map((city) => ({
    id: city.id,
    label: city.name,
  }));

  const typesNames = Object.values(schoolTypes).map((type) => type.name);
  const typesSelectOptions = Object.values(schoolTypes).map((type) => ({
    id: type.id,
    label: type.name,
  }));

  const nameValidationSchema = Yup.string().required(
    'Название школы не может быть пустым',
  );

  const codeValidationSchema = Yup.string().required(
    'Код школы не может быть пустым',
  );

  const cityValidationSchema = Yup.string().oneOf(
    citiesNames,
    'Указанного города не существует',
  );

  const typeValidationSchema = Yup.string().oneOf(
    typesNames,
    'Указанного типа ОО не существует',
  );

  return (
    <Row destroy={destroy}>
      <InputCell
        value={name}
        onConfirm={(value) => update({ name: value })}
        validationSchema={nameValidationSchema}
        onValidationFailure={(error) => showError(error)}
      />

      <SearchCell
        value={schoolTypes[typeId]?.name ?? ''}
        options={typesSelectOptions}
        onConfirm={(value) => update({ typeId: value })}
        validationSchema={typeValidationSchema}
        onValidationFailure={(error) => showError(error)}
      />

      <InputCell
        value={code}
        onConfirm={(value) => update({ code: value })}
        validationSchema={codeValidationSchema}
        onValidationFailure={(error) => showError(error)}
      />

      <SearchCell
        value={cities[cityId]?.name ?? ''}
        options={citiesSelectOptions}
        onConfirm={(value) => update({ cityId: value })}
        validationSchema={cityValidationSchema}
        onValidationFailure={(error) => showError(error)}
      />
    </Row>
  );
};

export default SchoolsTableRow;
